import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APImage } from '@components/atoms/APImage';
import { Typography } from '@components/atoms/Typography';
import { showAmount } from '@utils/currency';

import { TOrderSummaryCardProps } from './types';

export const OrderSummaryCard = ({ items, summary, currency }: TOrderSummaryCardProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const firstItem = items[0];
  const totalQty = items.reduce((sum, item) => sum + (item.amount || 1), 0);
  const hasMultiple = items.length > 1;

  const metaText = hasMultiple
    ? `${totalQty} ${t('Checkout.units')}`
    : `${totalQty} × ${firstItem.name}`;

  const showFees = summary.fees !== undefined && summary.fees.value > 0;
  const showShipping = summary.shipping !== undefined;
  const isFreeShipping = summary.shipping?.value === 0;
  const hasSubtotals = showFees || showShipping;

  return (
    <div className="bg-white rounded-[16px] p-[14px]">
      {/* Compact header — always visible */}
      <div className="flex items-center gap-3">
        {firstItem.thumbnail && (
          <APImage
            img={firstItem.thumbnail}
            width={44}
            height={44}
            alt={firstItem.name}
            className="!mr-0 rounded-lg object-cover !w-11 !h-11 flex-shrink-0"
          />
        )}

        <div className="flex-1 min-w-0">
          <Typography
            text={firstItem.name}
            weight={700}
            className="text-[15px] text-[#0F1514] leading-[1.2] truncate"
          />
          <Typography text={metaText} className="text-[12px] text-[#8A908F] mt-[2px]" />
        </div>

        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="text-[13px] font-semibold text-[#0F1514] underline underline-offset-[3px] bg-transparent border-0 cursor-pointer p-0 flex-shrink-0"
        >
          {isOpen ? t('Checkout.hideOrderDetails') : t('Checkout.showOrderDetails')}
        </button>
      </div>

      {/* Expanded content */}
      {isOpen && (
        <>
          <div className="h-[1px] bg-[#ECECEA] my-3" />

          <div className="flex flex-col gap-2">
            {items.map((item, idx) => (
              <div key={item.id ?? idx} className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <Typography
                    text={item.name}
                    weight={700}
                    className="text-[15px] text-[#0F1514] leading-[1.2]"
                  />
                  {item.description && (
                    <Typography
                      text={item.description}
                      className="text-[13px] text-[#4A4F4E] leading-[1.3]"
                    />
                  )}
                  {item.amount > 1 && (
                    <Typography
                      text={`${item.amount} × ${showAmount({ value: item.price, currency })}`}
                      className="text-[13px] text-[#8A908F] mt-[2px]"
                    />
                  )}
                </div>
                <Typography
                  text={showAmount({ value: item.price * (item.amount || 1), currency })}
                  weight={600}
                  className="text-[15px] text-[#0F1514] flex-shrink-0"
                />
              </div>
            ))}
          </div>

          {hasSubtotals && (
            <>
              <div className="h-[1px] bg-[#ECECEA] my-3" />

              <div className="flex flex-col gap-2">
                {showShipping && (
                  <div className="flex justify-between">
                    <Typography
                      text={summary.shipping?.label}
                      className="text-[14px] text-[#4A4F4E]"
                    />
                    {isFreeShipping ? (
                      <Typography
                        textKey="Checkout.free"
                        className="text-[14px] text-[#4d8e25]"
                        weight={500}
                      />
                    ) : (
                      <Typography
                        text={showAmount({ value: summary.shipping?.value ?? 0, currency })}
                        className="text-[14px] text-[#0F1514]"
                        weight={500}
                      />
                    )}
                  </div>
                )}

                {showFees && (
                  <div className="flex justify-between">
                    <Typography text={summary.fees?.label} className="text-[14px] text-[#4A4F4E]" />
                    <Typography
                      text={showAmount({ value: summary.fees?.value ?? 0, currency })}
                      className="text-[14px] text-[#0F1514]"
                      weight={500}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
